import { useState } from 'react';
import { resolveApiError, useTranslation } from '@eon/i18n';
import {
  Badge,
  Button,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  PageHeader,
  Pagination,
  toast,
  type Column,
} from '@eon/ui';
import { hooks, type OmUser } from '@eon/api-client';
import { formatDate, formatKWh, formatRelative } from '@eon/utils';
import { usePaginationLabels } from '@/lib/use-pagination-labels';
import { useTableSort } from '@/lib/table-sort';
import {
  Battery,
  History,
  KeyRound,
  Mail,
  MessageSquare,
  MoreVertical,
  Pencil,
  Search,
  Trash2,
  UserCheck,
  UserPlus,
} from 'lucide-react';
import { AllowanceBalance } from '@/components/AllowanceBalance';
import { AllowanceDialog } from '@/components/portal/AllowanceDialog';
import { AllowanceHistoryDialog } from '@/components/portal/AllowanceHistoryDialog';
import { EditOmUserDialog } from '@/components/portal/EditOmUserDialog';
import { InviteUserDialog } from '@/components/portal/InviteUserDialog';
import { ManageRfidDialog } from '@/components/portal/ManageRfidDialog';
import { PendingInvitationsDialog } from '@/components/portal/PendingInvitationsDialog';

const PAGE_SIZE_OPTIONS = [25, 50, 100];

export function UsersPage() {
  const { t } = useTranslation();
  const labels = usePaginationLabels();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [q, setQ] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [editUser, setEditUser] = useState<OmUser | null>(null);
  const [allowanceUser, setAllowanceUser] = useState<OmUser | null>(null);
  const [historyUser, setHistoryUser] = useState<OmUser | null>(null);
  const [rfidUser, setRfidUser] = useState<OmUser | null>(null);

  const { sort, onSortChange, sortParams } = useTableSort(() => setPage(1));
  const list = hooks.useMyUsers({ page, pageSize, q: q.trim() || undefined, ...sortParams });
  const remove = hooks.useOmRemoveUser();
  const activate = hooks.useOmActivateUser();
  const passwordReset = hooks.useOmSendPasswordReset();
  const testEmail = hooks.useOmTestEmail();
  const testSms = hooks.useOmTestSms();

  const onRemove = (user: OmUser) => {
    if (!window.confirm(t('portal.users.removeConfirm'))) return;
    remove.mutate(user.userId, {
      onSuccess: () => toast.success(t('portal.users.removed')),
      onError: (e) => toast.error(resolveApiError(e, t)),
    });
  };

  const columns: Column<OmUser>[] = [
    { key: 'name', header: t('user.name'), sortable: true, cell: (u) => u.name ?? '—' },
    { key: 'phone', header: t('user.phone'), sortable: true, cell: (u) => u.phone ?? '—' },
    {
      key: 'email',
      header: t('user.email'),
      sortable: true,
      cell: (u) => <span className="font-mono text-xs">{u.email ?? '—'}</span>,
    },
    {
      key: 'lastCharge',
      header: t('portal.users.lastCharge'),
      cell: (u) => (
        <span className="text-muted-foreground text-xs">{formatRelative(u.lastChargeAt)}</span>
      ),
    },
    {
      key: 'rfid',
      header: t('portal.users.rfid'),
      cell: (u) => <span className="font-mono text-xs">{u.rfid ?? '—'}</span>,
    },
    {
      key: 'joinDate',
      header: t('portal.users.joinDate'),
      sortable: true,
      sortKey: 'joinDate',
      sortValue: (u) => u.joinDate ?? u.joinedAt,
      cell: (u) => formatDate(u.joinDate ?? u.joinedAt),
    },
    {
      key: 'remainingAllowance',
      header: t('portal.users.remainingAllowance'),
      sortable: true,
      sortKey: 'remainingAllowance',
      sortValue: (u) => u.remainingAllowance,
      cell: (u) => <AllowanceBalance value={u.remainingAllowance ?? u.allowanceKwh} />,
    },
    {
      key: 'totalConsume',
      header: t('portal.users.totalConsume'),
      cell: (u) => (u.totalConsume != null ? formatKWh(u.totalConsume) : '—'),
    },
    {
      key: 'status',
      header: t('common.status'),
      sortable: true,
      sortValue: (u) => u.active,
      cell: (u) => (
        <Badge variant={u.active ? 'success' : 'secondary'}>
          {u.active ? t('user.active') : t('user.inactive')}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12 text-right',
      cell: (u) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuItem onClick={() => setEditUser(u)}>
              <Pencil className="mr-2 h-4 w-4" />
              {t('actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setAllowanceUser(u)}>
              <Battery className="mr-2 h-4 w-4" />
              {t('portal.users.allowance')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setHistoryUser(u)}>
              <History className="mr-2 h-4 w-4" />
              {t('portal.users.allowanceHistory')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setRfidUser(u)}>
              <KeyRound className="mr-2 h-4 w-4" />
              {t('portal.users.manageRfid')}
            </DropdownMenuItem>
            {!u.active ? (
              <DropdownMenuItem
                onClick={() =>
                  activate.mutate(u.userId, {
                    onSuccess: () => toast.success(t('portal.users.activated')),
                    onError: (e) => toast.error(resolveApiError(e, t)),
                  })
                }
              >
                <UserCheck className="mr-2 h-4 w-4" />
                {t('portal.users.activate')}
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              onClick={() =>
                passwordReset.mutate(u.userId, {
                  onSuccess: () => toast.success(t('portal.users.passwordResetSent')),
                  onError: (e) => toast.error(resolveApiError(e, t)),
                })
              }
            >
              <KeyRound className="mr-2 h-4 w-4" />
              {t('portal.users.sendPasswordReset')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                testEmail.mutate(u.userId, {
                  onSuccess: () => toast.success(t('portal.users.testEmailSent')),
                  onError: (e) => toast.error(resolveApiError(e, t)),
                })
              }
            >
              <Mail className="mr-2 h-4 w-4" />
              {t('portal.users.testEmail')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                testSms.mutate(u.userId, {
                  onSuccess: () => toast.success(t('portal.users.testSmsSent')),
                  onError: (e) => toast.error(resolveApiError(e, t)),
                })
              }
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              {t('portal.users.testSms')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-destructive" onClick={() => onRemove(u)}>
              <Trash2 className="mr-2 h-4 w-4" />
              {t('portal.users.remove')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const totalCount = list.data?.totalCount ?? 0;
  const totalPages = list.data?.totalPages ?? 1;

  return (
    <>
      <PageHeader
        title={t('nav.users')}
        description={list.data ? `${totalCount.toLocaleString()} ${t('nav.users')}` : undefined}
        actions={
          <>
            <Button variant="outline" onClick={() => setPendingOpen(true)}>
              {t('portal.users.pendingInvitations')}
            </Button>
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              {t('portal.users.invite')}
            </Button>
          </>
        }
      />

      <div className="mb-4 max-w-md">
        <div className="relative">
          <Search className="text-muted-foreground absolute top-2.5 left-3 h-4 w-4" />
          <Input
            className="pl-9"
            placeholder={t('portal.users.searchPlaceholder')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
          />
        </div>
      </div>

      <DataTable
        data={list.data?.data}
        columns={columns}
        loading={list.isLoading}
        rowKey={(u) => u.id}
        emptyTitle={t('user.notFound')}
        sort={sort}
        onSortChange={onSortChange}
      />

      {list.data && totalCount > 0 ? (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          loading={list.isFetching}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          labels={labels}
        />
      ) : null}

      <InviteUserDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <PendingInvitationsDialog open={pendingOpen} onOpenChange={setPendingOpen} />
      <EditOmUserDialog user={editUser} open={Boolean(editUser)} onOpenChange={(o) => !o && setEditUser(null)} />
      <AllowanceDialog
        user={allowanceUser}
        open={Boolean(allowanceUser)}
        onOpenChange={(o) => !o && setAllowanceUser(null)}
      />
      <AllowanceHistoryDialog
        user={historyUser}
        open={Boolean(historyUser)}
        onOpenChange={(o) => !o && setHistoryUser(null)}
      />
      <ManageRfidDialog user={rfidUser} open={Boolean(rfidUser)} onOpenChange={(o) => !o && setRfidUser(null)} />
    </>
  );
}
